import { useEffect, useState } from 'react'

import {
  Box,
  Button,
  Icon,
  Modal,
  Switch,
  Text,
} from '@fairfashion/design-system'
import { toPng } from 'html-to-image'
import {
  ReactFlow,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  Panel,
  getRectOfNodes,
  getTransformForBounds,
} from 'reactflow'

import 'reactflow/dist/style.css'
import { SegmentsDataProvider } from '../../contexts/segments'

import { TraceabilityTreeProps } from './index.types'
import * as Styles from './traceability-tree.styles'
import { getLayoutedElements } from './utils/getLayoutedElements'

const TraceabilityTreeComp = ({
  initialNodes,
  initialEdges,
  nodeTypes,
  selected,
  centerNodeId,
  expand,
  setExpand,
  showExpand,
}: TraceabilityTreeProps & {
  expand: boolean
  setExpand: (expand: boolean) => void
}) => {
  const [direction, setDirection] = useState<'LR' | 'TB'>('TB')
  const [nodes, setNodes] = useNodesState([])
  const [edges, setEdges] = useEdgesState([])
  const [loaded, setLoaded] = useState(false)

  const [nodeWidth, setNodeWidth] = useState<number>()
  const [nodeHeight, setNodeHeight] = useState<number>()

  const { fitView, getZoom, getNodes } = useReactFlow()

  const loadLayout = () => {
    if (!initialNodes?.length) return

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      initialNodes,
      initialEdges,
      direction,
      nodeWidth,
      nodeHeight,
    )

    // This is a trick to ensure that the nodes are updated correctly
    setNodes(
      layoutedNodes.map((node) => ({
        ...node,
        data: { ...node.data },
      })),
    )
    setEdges(layoutedEdges.map((edge) => ({ ...edge })))
  }

  const setCenterToRoot = () => {
    const zoom = getZoom()

    let centerNode = nodes[0]
    if (selected && selected.length) {
      const foundNode = nodes.find((node) => node.id === selected[0].id)

      if (foundNode) {
        centerNode = foundNode
      }
    }

    if (centerNodeId) {
      const foundNode = nodes.find((node) => node.id === centerNodeId)

      if (foundNode) {
        centerNode = foundNode
      }
    }

    const _centerNodes = centerNode ? [centerNode] : ([] as any)

    fitView({ nodes: _centerNodes, maxZoom: zoom, minZoom: zoom })
  }

  const loadTree = () => {
    // Render first to get node width and height
    loadLayout()
    // This interval is used to guarantee that nodes will be rendered correctly
    const interval = setInterval(() => {
      const element: HTMLDivElement | null = document.querySelector(`[data-id]`)

      // The second child element is the one that contains the nodes
      const secondChildElement = element?.children[1] as HTMLDivElement

      // Set the width and height of the dagre node to match the custom node
      setNodeWidth(secondChildElement?.offsetWidth ?? 0)
      setNodeHeight(secondChildElement?.offsetHeight ?? 0)

      // If the second child element exists, it means that the nodes have been rendered
      if (secondChildElement && nodeWidth && nodeHeight) {
        loadLayout()
        setCenterToRoot()
        setTimeout(() => {
          setLoaded(true)
        }, 500)
        clearInterval(interval)
      }
    }, 100)
  }

  useEffect(() => {
    loadTree()
  }, [initialEdges, initialNodes, direction, fitView, nodeWidth, nodeHeight])

  useEffect(() => {
    const userDirection = localStorage.getItem('tree-direction')
    if (userDirection) {
      setDirection(userDirection as 'LR' | 'TB')
    }
  }, [])

  const toggleDirection = () => {
    const newDirection = direction === 'LR' ? 'TB' : 'LR'
    setDirection(newDirection)
    localStorage.setItem('tree-direction', newDirection)
    loadTree()
  }

  const downloadImage = (dataUrl: string) => {
    const a = document.createElement('a')

    a.setAttribute('download', 'imagem.png')
    a.setAttribute('href', dataUrl)
    a.click()
  }

  const onDownload = () => {
    const nodesBounds = getRectOfNodes(getNodes())

    const imageWidth = nodesBounds.width
    const imageHeight = nodesBounds.height

    const transform = getTransformForBounds(
      nodesBounds,
      imageWidth,
      imageHeight,
      0.5,
      2,
    )

    const dpi = 200
    const scale = dpi / 96

    toPng(document.querySelector('.react-flow__viewport') as any, {
      backgroundColor: 'none',
      width: imageWidth,
      height: imageHeight,
      canvasWidth: imageWidth * scale,
      canvasHeight: imageHeight * scale,
      style: {
        width: `${imageWidth}px`,
        height: `${imageHeight}px`,
        transform: `translate(${transform[0]}px, ${transform[1]}px) scale(${transform[2]})`,
      },
    }).then(downloadImage)
  }

  return (
    <Styles.Container
      style={{
        opacity: loaded ? 1 : 0,
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodesConnectable={false}
        elementsSelectable={false}
        nodeTypes={nodeTypes}
        minZoom={0.5}
        maxZoom={3}
      >
        <Background color="#666" gap={16} />
        <MiniMap pannable style={{ cursor: 'grab' }} />
        <Panel position="top-right">
          <Box gap={1} alignItems="center">
            <Button
              variant="outlined"
              onClick={onDownload}
              padding="8px 12px"
              height="44px"
              backgroundColor="white"
            >
              <Styles.ButtonText size="small" fontWeight="normal">
                Exportar PNG
              </Styles.ButtonText>
              <Icon name="download" size={14} color="grey_1" />
            </Button>
            <Styles.TopPanel>
              <Text size="small">Horizontal</Text>
              <Switch
                onChange={() => toggleDirection()}
                checked={direction === 'TB'}
              />
              <Text size="small">Vertical</Text>
            </Styles.TopPanel>

            {showExpand && (
              <Styles.MapButton onClick={() => setExpand(!expand)}>
                {expand ? (
                  <Icon name="fullscreen_exit" size={20} color="grey_2" />
                ) : (
                  <Icon name="fullscreen" size={20} color="grey_2" />
                )}
              </Styles.MapButton>
            )}
          </Box>
        </Panel>
      </ReactFlow>
    </Styles.Container>
  )
}

export const TraceabilityTree = (props: TraceabilityTreeProps) => {
  const [expand, setExpand] = useState(false)

  return (
    <ReactFlowProvider>
      <SegmentsDataProvider>
        {expand ? (
          <Modal
            open={expand}
            onClose={() => setExpand(false)}
            title={'Rastreabilidade'}
            width="100%"
            height="100%"
            minHeight="100%"
            minWidth="100%"
          >
            <TraceabilityTreeComp
              expand={expand}
              setExpand={setExpand}
              {...props}
            />
          </Modal>
        ) : (
          <TraceabilityTreeComp
            expand={expand}
            setExpand={setExpand}
            {...props}
          />
        )}
      </SegmentsDataProvider>
    </ReactFlowProvider>
  )
}
